import { Card, Flex, Text, IconButton, Button } from "@hcsc-fiber/react";
import React, { useContext, useState } from "react";
import { defaultTrustedDeviceNameFromCIDM } from "../../constants/static";
import { StateContext } from "../../context/stateContext";
import { translations } from "../../translate";
import styles from "./DeviceCard.module.scss";

import { Device, PassKey } from "./TrustedDevice.type";

const shortDateOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};
const detailedDateOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
};

export type DeviceCardProps = {
  device?: Device;
  onRemove?: (_: Device) => void;
  key: string;
  passKey?: PassKey;
  onRemovePassKey?: (_: PassKey) => void;
  closeAlert: () => void;
};

export const DeviceCard: React.FC<DeviceCardProps> = props => {
  const [isCardOpen, setIsCardOpen] = useState(false);
  const { languageCode } = useContext(StateContext);
  const { deviceInfo, lastSelectedDate, uuid, deviceName } = props.device || {};
  const vendor = deviceInfo?.device?.vendor ?? "";
  const model = deviceInfo?.device?.model ?? "";
  const browserName = (deviceInfo?.browser?.name ?? "") + " Browser";
  const osName = deviceInfo?.os?.name ?? "";
  const osVersion = deviceInfo?.os?.version ?? "";
  const date = new Date(lastSelectedDate ?? "");
  const detailedDateString = date.toLocaleString(`${languageCode}-US`, detailedDateOptions);
  const shortDateString = date.toLocaleString(`${languageCode}-US`, shortDateOptions);

  return (
    <Card className={styles.cardContainer}>
      <Flex alignItems="stretch" direction="column">
        <Flex.Item>
          <Flex justifyContent="space-between" direction="row">
            {(deviceName != "" && deviceName != defaultTrustedDeviceNameFromCIDM) || props.passKey ? (
              <Flex.Item>
                <Text as="p">
                  <strong>{props.passKey ? props.passKey.key : deviceName}</strong>
                </Text>
              </Flex.Item>
            ) : (
              <Flex.Item>
                {!props.passKey && (
                  <>
                    <Text as="p">
                      <strong>{vendor}</strong> <strong>{model}</strong>
                      {!vendor && !model && <strong>{osName}</strong>}
                    </Text>
                    <Text as="p">
                      <strong>{browserName}</strong>
                    </Text>
                  </>
                )}
              </Flex.Item>
            )}
            <Flex.Item>
              {!props.passKey && (
                <Text as="p" textAlign="right">
                  {shortDateString}
                </Text>
              )}
            </Flex.Item>
          </Flex>
        </Flex.Item>
        {isCardOpen && (
          <Flex.Item>
            {!props.passKey && (
              <Flex justifyContent="space-between">
                <Text as="p" size={1}>
                  {translations.lastAccessed[languageCode]}:
                </Text>
                <Text as="p" size={1} textAlign="left">
                  {detailedDateString}
                </Text>
              </Flex>
            )}
            {!props?.passKey && (
              <Flex justifyContent="space-between" margin={[2, 0, 0, 0]}>
                <Text as="p" size={1}>
                  {translations.operatingSystem[languageCode]}
                </Text>
                <Text as="p" size={1}>
                  {osName} {osVersion}
                </Text>
              </Flex>
            )}
            <Flex justifyContent="center" className={styles.removeButtonContainer}>
              <Button
                kind="secondary"
                data-testid={`removeDevice${props.device?.uuid}`}
                onClick={() => {
                  if (props.device && props.onRemove && props.closeAlert) {
                    props.onRemove(props.device);
                    props?.closeAlert();
                  } else if (props.passKey && props.onRemovePassKey && props.closeAlert) {
                    props.onRemovePassKey(props.passKey);
                    props.closeAlert();
                  }
                }}
              >
                {props.passKey ? translations.removePasskey[languageCode] : translations.removeDevice[languageCode]}
              </Button>
            </Flex>
          </Flex.Item>
        )}
        <Flex justifyContent="space-between">
          <Flex.Item className={styles.removeButtonBottom}>
            {isCardOpen && (
              <Button
                kind="secondary"
                onClick={() => {
                  if (props.device && props.onRemove && props.closeAlert) {
                    props.onRemove(props.device);
                    props?.closeAlert();
                  } else if (props.passKey && props.onRemovePassKey && props.closeAlert) {
                    props.onRemovePassKey(props.passKey);
                    props?.closeAlert();
                  }
                }}
              >
                {props.passKey ? translations.removePasskey[languageCode] : translations.removeDevice[languageCode]}
              </Button>
            )}
          </Flex.Item>
          <div
            className={styles.iconButton}
            data-testid={`cardOpen${props.device?.uuid}`}
            onClick={() => setIsCardOpen(!isCardOpen)}
          >
            <Text as="span" color="blue-primary">
              {!isCardOpen ? translations.showMore[languageCode] : translations.showLess[languageCode]}
            </Text>
            <IconButton
              className={styles.iconButton}
              name={!isCardOpen ? "chevron-down" : "chevron-up"}
              color="blue-primary"
              size={2}
            >
              {""}
            </IconButton>
          </div>
        </Flex>
      </Flex>
    </Card>
  );
};
